import { cmp, isHexId, list, serialize, str } from '../kql';
import { normalizeId } from '../ids';
import { KQL_MAX_LENGTH, lookupSearchFields, lookupSearchQuery, titleElementOf } from '../lookupSearch';
import { cellText } from './columns';
import { splitValues } from './cellValues';

/**
 * A spreadsheet names records and people; ingest takes ids (KPR-1899). Every
 * name of a column is settled once, before the dry run, and only through what
 * this person can already read: the list endpoint in the scope the lookup's
 * picker reads, and the collection's roster. So a record they cannot read is
 * not found, exactly as ingest would answer.
 *
 * A name is never guessed. Two records answering to one name are that row's
 * error; the person says which by giving the record a name only it has.
 *
 * -> { [lowercased name]: { id } | { ambiguous: count } | { unchecked: true } };
 * a name absent from the answer was not found. `unchecked` is a name past what
 * one check may ask: it is not known to be missing, and is not said to be.
 */

const NAMES_PER_QUERY = 50;
const PAGE = 200;
// Each of these is a request of its own, so they are held to a number that
// fits the read budget (300 a minute).
const MAX_SEARCHED_NAMES = 200;
const MAX_EMAILS = 100;
const WORKERS = 4;
// A name is a few words; a cell this long is a sentence, and would not fit a query.
const MAX_NAME_LENGTH = 200;

const fold = text => String(text).trim().toLowerCase();

/** Every name a column's cells hold: a whole cell, and its pieces where the element takes several. */
export function namesInColumn(element, cells) {
	const names = new Set();
	cells.forEach(cell => {
		const text = cellText(cell).trim();
		if (!text) return;
		names.add(text);
		if (element.allowMultiple) splitValues(text).forEach(piece => names.add(piece));
	});
	return [...names].filter(name => !isHexId(name));
}

const titleOf = (row, titleName) => {
	const value = titleName ? row?.elements?.[titleName] : null;
	return typeof value === 'string' ? value : '';
};

function answerOf(found, unchecked = []) {
	const out = Object.fromEntries([...found].map(([key, ids]) => [key, ids.size > 1 ? { ambiguous: ids.size } : { id: [...ids][0] }]));
	unchecked.forEach(name => { out[fold(name)] = { unchecked: true }; });
	return out;
}

function addMatch(found, name, id) {
	const key = fold(name);
	if (!key || !id) return;
	const ids = found.get(key) || new Set();
	ids.add(id);
	found.set(key, ids);
}

function record(found, rows, titleName) {
	rows.forEach(row => {
		const id = normalizeId(row?._id);
		addMatch(found, row?.displayValue, id);
		addMatch(found, titleOf(row, titleName), id);
	});
}

/** Names cut into `in` lists that fit KQL's limit on one query. */
export function nameBatches(names, path, forceElement) {
	const queryOf = batch => serialize(cmp(path, 'in', list(batch.map(name => str(name))), { forceElement }));
	const batches = [];
	let batch = [];
	names.forEach(name => {
		const next = [...batch, name];
		if (batch.length && (next.length > NAMES_PER_QUERY || queryOf(next).length > KQL_MAX_LENGTH)) {
			batches.push(batch);
			batch = [name];
		} else {
			batch = next;
		}
	});
	if (batch.length) batches.push(batch);
	return batches.map(queryOf);
}

async function readAllPages(fetchPage, scope, q, found, titleName) {
	let skip = 0;
	for (;;) {
		// eslint-disable-next-line no-await-in-loop
		const { rows, total } = await fetchPage({ ...scope, q, limit: PAGE, skip });
		record(found, rows, titleName);
		skip += rows.length;
		if (!rows.length || skip >= total) return;
	}
}

async function eachWorker(tasks, work) {
	let next = 0;
	const run = async () => {
		while (next < tasks.length) {
			const task = tasks[next];
			next += 1;
			// eslint-disable-next-line no-await-in-loop
			await work(task);
		}
	};
	await Promise.all(Array.from({ length: Math.min(WORKERS, tasks.length) }, run));
}

/**
 * The records a lookup's target card holds under `names`.
 *
 * First an `in` over the target's title element: one request per fifty names
 * however many records the card has. A target that reads through a display
 * template (a resident is First Last) has no element holding the whole name,
 * so what is left is searched by word and kept only on an exact match of the
 * name it displays.
 */
export async function resolveRecordNames({ collectionId, cardId, names, cardsById, getElementsForCard, fetchPage }) {
	const found = new Map();
	const askable = names.filter(name => name.length <= MAX_NAME_LENGTH);
	if (!askable.length) return {};
	const scope = { collectionId, cardId };
	const elements = (typeof getElementsForCard === 'function' ? getElementsForCard(cardId) : null) || [];
	const title = titleElementOf(elements);
	const fields = lookupSearchFields({ cardIds: [cardId], cardsById, getElementsForCard });
	const field = fields[0];
	if (field) {
		const queries = nameBatches(askable, field.path, field.forceElement);
		for (const q of queries) {
			// eslint-disable-next-line no-await-in-loop
			await readAllPages(fetchPage, scope, q, found, title?.name);
		}
	}
	let unchecked = [];
	if (fields.length > 1) {
		const missing = askable.filter(name => !found.has(fold(name)));
		unchecked = missing.slice(MAX_SEARCHED_NAMES);
		await eachWorker(missing.slice(0, MAX_SEARCHED_NAMES), async name => {
			const q = lookupSearchQuery({ fields, term: name });
			if (!q) return;
			const { rows } = await fetchPage({ ...scope, q, limit: PAGE });
			record(found, rows.filter(row => fold(row?.displayValue) === fold(name)), title?.name);
		});
	}
	return answerOf(found, unchecked);
}

const isEmail = text => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);

/**
 * The people `names` name: an email by exact search, which reaches anyone, and
 * a full name among the collection's collaborators, the people a user
 * element's picker lists first. That roster never carries an email, and a
 * card-scoped writer may not read it, which leaves them email only.
 */
export async function resolvePeople({ collectionId, names, getCollaborators, searchByEmail }) {
	const found = new Map();
	if (!names.length) return {};
	const emails = names.filter(isEmail);
	if (emails.length < names.length) {
		let roster = [];
		try {
			roster = await getCollaborators(collectionId);
		} catch (e) {
			if (e?.response?.status !== 403) throw e;
		}
		roster.forEach(person => addMatch(found, person?.fullName, normalizeId(person?._id)));
	}
	const unchecked = emails.slice(MAX_EMAILS);
	await eachWorker(emails.slice(0, MAX_EMAILS), async email => {
		const people = await searchByEmail(email);
		people.forEach(person => {
			if (fold(person?.email) === fold(email)) addMatch(found, email, normalizeId(person?._id));
		});
	});
	return answerOf(found, unchecked);
}
